import { FC, PropsWithChildren } from 'react'
import { styled } from '@portfolio/styles'
import { Link } from '../Link/Link'
import { Table } from './Table'
import {
  PortableText as SanityPortableText,
  PortableTextComponents,
  PortableTextMarkComponentProps,
  PortableTextBlockComponent,
  PortableTextListComponent,
  PortableTextListItemComponent,
} from '@portabletext/react'
import { TypedObject } from '@portabletext/types'
import { PortableTextBlocks } from '@portfolio/models'

type MarkComponent<T extends TypedObject = TypedObject> = (
  props: PropsWithChildren<PortableTextMarkComponentProps<T>>,
) => JSX.Element

export type PortableTextProps = {
  value?: PortableTextBlocks
  additionalComponents?: PortableTextComponents
  appearance?: 'default' | 'faq' | 'plain'
  // `disableTopBottomMargins` is handy when the PortableText is displayed in container with flex/grid which disables margin collapsing.
  disableTopBottomMargins?: boolean
}

const link: MarkComponent<{
  _type: string
  href: string
  linkType?: string
}> = ({ value, children }) => {
  if (!value?.href) {
    return <>{children}</>
  }

  return (
    <Link
      href={value.href}
      target={value?.linkType === 'external' ? '_blank' : undefined}
    >
      {children}
    </Link>
  )
}

const textColor: MarkComponent<{
  _type: 'textColor'
  color?: {
    hex?: string
    opacity?: number
    rgba?: string
  }
}> = ({ value, children }) => {
  const colorValue = value?.color?.rgba || value?.color?.hex || 'inherit'
  return <span style={{ color: colorValue }}>{children}</span>
}

const Body03Paragraph = styled('p', {
  projectFont: 'body03',
})

const richTextHeadingStyles = {
  h1: {
    marginTop: '1.5em',
    marginBottom: '.75em',
    projectFont: 'heading02',
  },
  h2: {
    marginTop: '1.5em',
    marginBottom: '.75em',
    projectFont: 'heading05',
  },
  h3: {
    marginTop: '1.5em',
    marginBottom: '.75em',
    projectFont: 'heading07',
  },
  h4: {
    marginTop: '1.25em',
    marginBottom: '.5em',
    projectFont: 'heading08',
  },
  h5: {
    marginTop: '1em',
    marginBottom: '.25em',
    projectFont: 'ui01',
  },
  h6: {
    marginTop: '.75em',
    marginBottom: '.25em',
    projectFont: 'ui04',
  },
  ':is(h1, h2, h3, h4, h5, h6) strong': {
    font: 'inherit',
    fontWeight: 'bold',
  },
} as const

export const PortableTextStylesWrapper = styled('div', {
  variants: {
    disableTopBottomMargins: {
      true: {
        '*:last-child': {
          marginBottom: 0,
        },
        '*:first-child': {
          marginTop: 0,
        },
      },
    },
    appearance: {
      default: {
        strong: {
          projectFont: 'body01_bold',
        },
        em: {
          fontStyle: 'italic',
        },
        projectFont: 'body01',
        b: {
          fontWeight: 'bold',
        },
        a: {
          textDecoration: 'underline',
          textUnderlineOffset: '5px',
          textDecorationThickness: '1px',
          '&:hover': {
            opacity: '0.5',
          },
        },
        ...richTextHeadingStyles,
        blockquote: {
          projectFont: 'heading06',
          margin: '$64 0',
          textAlign: 'center',
        },
        'p, ul, ol': {
          marginTop: '1em',
          marginBottom: '1em',
          listStyle: 'inside',
        },
        'ul, ol': {
          paddingLeft: '$28',
        },
        ol: {
          listStyle: 'none',
          counterReset: 'ordered-list',
        },
        'ol > li': {
          position: 'relative',
          '&:before': {
            content: 'counter(ordered-list)"."',
            counterIncrement: 'ordered-list',
            position: 'absolute',
            right: '100%',
            projectFont: 'body01',
            marginRight: '8px',
          },
        },
        'ul > li': {
          listStyleType: 'none',
          marginBottom: '$16',
          marginTop: '$16',
          '&:before': {
            content: '–',
            position: 'absolute',
            marginLeft: '-$20',
          },
        },
      },
      faq: {
        projectFont: 'body03',
        strong: {
          projectFont: 'body03_bold',
        },
        em: {
          fontStyle: 'italic',
        },
        b: {
          fontWeight: 'bold',
        },
        a: {
          textDecoration: 'underline',
          textUnderlineOffset: '5px',
          textDecorationThickness: '1px',
          '&:hover': {
            opacity: '0.5',
          },
        },
        button: {
          projectFont: 'body03',
          textDecoration: 'underline',
          textUnderlineOffset: '5px',
          textDecorationThickness: '1px',
          '&:hover': {
            opacity: '0.5',
          },
        },
        ...richTextHeadingStyles,
        blockquote: {
          projectFont: 'heading06',
          margin: '$64 0',
          textAlign: 'center',
        },
        'p, ul, ol': {
          marginTop: '1em',
          marginBottom: '1em',
          listStyle: 'inside',
        },
        ul: {
          paddingLeft: '$28',
        },
        'ul > li': {
          listStyleType: 'none',
          marginBottom: '$16',
          marginTop: '$16',

          '&:before': {
            content: '–',
            position: 'absolute',
            marginLeft: '-$20',
          },
        },
      },
      plain: {},
    },
  },
})

export const PortableText: FC<PortableTextProps> = ({
  value,
  additionalComponents,
  appearance = 'default',
  disableTopBottomMargins,
}) => {
  const components: PortableTextComponents = {
    block: {
      // Default block styles are handled by CSS via styled components
      // but we need to ensure blocks render as proper HTML elements
      h1: ({ children }) => <h1>{children}</h1>,
      h2: ({ children }) => <h2>{children}</h2>,
      h3: ({ children }) => <h3>{children}</h3>,
      h4: ({ children }) => <h4>{children}</h4>,
      h5: ({ children }) => <h5>{children}</h5>,
      h6: ({ children }) => <h6>{children}</h6>,
      blockquote: ({ children }) => <blockquote>{children}</blockquote>,
      normal: ({ children }) => <p>{children}</p>,
      body03: ({ children }) => <Body03Paragraph>{children}</Body03Paragraph>,
      ...((additionalComponents?.block || {}) as Record<
        string,
        PortableTextBlockComponent
      >),
    },
    list: {
      bullet: ({ children }) => <ul>{children}</ul>,
      number: ({ children }) => <ol>{children}</ol>,
      ...((additionalComponents?.list || {}) as Record<
        string,
        PortableTextListComponent
      >),
    },
    listItem: {
      bullet: ({ children }) => <li>{children}</li>,
      number: ({ children }) => <li>{children}</li>,
      ...((additionalComponents?.listItem || {}) as Record<
        string,
        PortableTextListItemComponent
      >),
    },
    marks: {
      link,
      textColor,
      ...(additionalComponents?.marks || {}),
    },
    types: {
      sectionTable: (props) => <Table value={props.value} />,
      ...(additionalComponents?.types || {}),
    },
  }

  return value ? (
    <PortableTextStylesWrapper
      appearance={appearance}
      disableTopBottomMargins={disableTopBottomMargins}
    >
      <SanityPortableText value={value} components={components} />
    </PortableTextStylesWrapper>
  ) : null
}
